import { CollectionImage } from "@/app/collections/view/collection-image"
import { useIsMobileEmailUser } from "@/app/hooks/use-is-mobile-email-user"
import { getCollectionHref } from "@/app/navigation/collection"
import type { CollectionMiniature } from "@anaconda/core/nft-collection/types"
import { Trans } from "@lingui/react/macro"
import Link from "next/link"
import { useScanner } from "../anaconda-sdk-scanner"
import { AppToast, SuccessViewTransaction } from "./app-toast"
import { useToastError } from "./toast-util"

export const CollectionBidToast = {
  Error: function CollectionBidToastError({
    collection,
    id,
    error,
  }: {
    collection: CollectionMiniature
    id: string | number
    error: unknown
  }) {
    const { name, image } = collection
    const { message, type } = useToastError(error, collection.blockchain)

    const title = getCollectionBidText({ collection })
    const main = <CollectionBidLink collection={collection} />

    if (type === "destructive") {
      return (
        <AppToast.Error
          id={id}
          title={title}
          image={<CollectionImage collectionName={name} imageSrc={image} />}
          main={main}
          second={
            <div className="font-semibold text-destructive">{message}</div>
          }
        />
      )
    } else if (type === "surface") {
      return (
        <AppToast.Warning
          id={id}
          image={<CollectionImage collectionName={name} imageSrc={image} />}
          title={title}
          main={main}
          second={
            <div className="font-semibold text-muted-foreground">{message}</div>
          }
        />
      )
    }
  },
  Pending: function CollectionBidToastPending({
    collection,
    id,
  }: {
    collection: CollectionMiniature
    id: string | number
  }) {
    const { name, image } = collection
    const isMobileEmailUser = useIsMobileEmailUser()

    if (isMobileEmailUser) {
      return null
    }

    return (
      <AppToast.Pending
        id={id}
        image={<CollectionImage collectionName={name} imageSrc={image} />}
        title={getCollectionBidText({ collection })}
        main={<CollectionBidLink collection={collection} />}
        second={
          <div className="font-semibold text-muted-foreground">
            <Trans>Pending...</Trans>
          </div>
        }
      />
    )
  },
  Success: function CollectionBidToastSuccess({
    id,
    collection,
    transactionId,
  }: {
    collection: CollectionMiniature
    id: string | number
    transactionId: string
  }) {
    const { name, image } = collection
    return (
      <AppToast.Success
        id={id}
        image={<CollectionImage collectionName={name} imageSrc={image} />}
        title={getCollectionBidText({ collection })}
        main={<CollectionBidLink collection={collection} />}
        second={
          <SuccessViewTransaction
            transactionId={transactionId}
            blockchain={collection.blockchain}
          />
        }
      />
    )
  },
}

function CollectionBidLink({
  collection,
}: { collection: CollectionMiniature }) {
  const { name, id } = collection
  const itemHref = getCollectionHref(id)
  return (
    <Trans>
      Add collection bid{" "}
      <Link href={itemHref} className="text-brand">
        {name}
      </Link>
    </Trans>
  )
}

function getCollectionBidText({
  collection,
}: { collection: CollectionMiniature }) {
  const { name } = collection
  const title = `Collection bid ${name}`
  return title
}
